import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import {
  trans,
  getPagePath,
  getResourcePath,
  useSoyuzRequest
} from '@spotahome/soyuz/client';

import {
  HeaderContainer,
  ToastContainer,
  FooterSimplified
} from '@spotahome/ui-library';

import '@spotahome/landings-common/src/scss/init.scss';

import { Mediator } from '@spotahome/soyuz-mediator';

import LazyLoginSignUpModal from '@spotahome/landings-common/src/components/LoginSignupModal';
import Hero from '@spotahome/landings-common/src/components/Hero';

import graphqlClient from '@spotahome/landings-common/src/graphql/client';
import { redeemReferralCodeByAccountId } from '@spotahome/landings-common/src/graphql/mutations/referee';
import { getReferrerDetailsByCode } from '@spotahome/landings-common/src/graphql/queries/referral';
import { getLoggedInUserInfo } from '@spotahome/landings-common/src/api/auth-client';

import RefereeHowItWorks from './components/RefereeHowItWorks';
import RefereeForm from './components/RefereeForm';
import WhySpotAHome from './components/WhySpotAHome';
import ErrorReferral from './components/ErrorReferral';

import './RefereeContainer.scss';

const redirectToHome = () => {
  window.location = getPagePath('landing');
};

const RefereeContainer = ({ loggedIn = false }) => {
  const [referrerName, setReferrerName] = useState('...');
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { params } = useSoyuzRequest();
  const referrerCode = params.referrerCode;

  const redeemReferralCode = useCallback(async () => {
    await redeemReferralCodeByAccountId(graphqlClient())({
      referralCode: referrerCode
    });
  }, [referrerCode]);

  const redeemReferralCodeAndRedirectToHome = useCallback(async () => {
    try {
      await redeemReferralCode();
      redirectToHome();
    } catch (error) {
      setHasError(true);
    }
  }, [redeemReferralCode]);

  const handleModalOpen = () => {
    Mediator.publish('AUTH_MODAL_OPEN', {
      initialAction: 'signup'
    });
  };

  useEffect(() => {
    const getActiveUserRedeemAndRedirect = async () => {
      setIsLoading(true);
      try {
        const { data: user } = await getLoggedInUserInfo();
        if (user.firstLogin) {
          redeemReferralCodeAndRedirectToHome();
        } else {
          redirectToHome();
        }
      } catch (error) {
        setHasError(true);
      }
    };
    if (loggedIn) {
      getActiveUserRedeemAndRedirect();
    }
  }, [loggedIn, redeemReferralCodeAndRedirectToHome]);

  useEffect(() => {
    const getRefererNameByCode = async () => {
      try {
        const response = await getReferrerDetailsByCode(graphqlClient())({
          input: { referralCode: referrerCode }
        });
        const { firstName, lastName } = response.data.getReferrerDetailsByCode;
        setReferrerName(`${firstName} ${lastName}`);
      } catch (error) {
        setHasError(true);
      }
    };
    getRefererNameByCode();
  }, [referrerCode]);

  const handleProfileChoice = async profileChoice => {
    const { isLandlord, isTenant } = profileChoice;
    if (isLandlord) {
      window.location = getPagePath('externals.landlord-landing-signup', {
        query: { referrerCode }
      });
    }
    if (isTenant) {
      handleModalOpen();
    }
  };

  const renderRefereeForm = () => (
    <div>
      <Hero
        menu={
          <HeaderContainer
            login={false}
            showMobileMenu={false}
            links={{
              home: false,
              listYourProperty: false,
              howItWorks: false,
              favorites: false
            }}
          />
        }
        title={trans('referee.hero.title', { referrerName })}
        subtitle={trans('referee.hero.subtitle')}
        size="big"
        colorTitle="dark"
        colorSubtitle="dark"
        isRowAlignContent
        className="referee__wrapper referee__wrapper--fit"
        classNameImage="referee__hero__image"
        image={getResourcePath('/images/referee/referee-illustration.svg')}
      />
      <div className="form__section__wrapper">
        {isLoading ? (
          <span>{trans('loading')}...</span>
        ) : (
          <RefereeForm onFormSubmission={handleProfileChoice} />
        )}
      </div>
    </div>
  );

  return (
    <div>
      {hasError && <ErrorReferral />}
      {!hasError && renderRefereeForm()}
      <ToastContainer />
      <RefereeHowItWorks />
      <WhySpotAHome onProfileChoice={handleProfileChoice} />
      <LazyLoginSignUpModal
        onTraditionalSignupSuccess={redeemReferralCodeAndRedirectToHome}
        onTraditionalLoginSuccess={redirectToHome}
      />
      <FooterSimplified />
    </div>
  );
};

RefereeContainer.propTypes = {
  loggedIn: PropTypes.bool
};

export default RefereeContainer;
